import { useMemo } from "react";

interface FloatingShapesProps {
  seed?: number;
  count?: number;
}

function seededRandom(seed: number) {
  let s = seed;
  return () => {
    s = (s * 16807) % 2147483647;
    return (s - 1) / 2147483646;
  };
}

export default function FloatingShapes({ seed = 42, count = 10 }: FloatingShapesProps) {
  const shapes = useMemo(() => {
    const rng = seededRandom(seed);
    const types = ["circle", "diamond", "plus", "dots", "line"] as const;
    return Array.from({ length: count }, (_, i) => ({
      type: types[Math.floor(rng() * types.length)],
      x: rng() * 90 + 5,     // 5-95%
      y: rng() * 90 + 5,
      size: 20 + rng() * 60,  // 20-80px
      speed: 0.02 + rng() * 0.13,
      rotation: rng() * 360,
      opacity: 0.04 + rng() * 0.08,
      key: i,
    }));
  }, [seed, count]);

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        overflow: "hidden",
        pointerEvents: "none",
        zIndex: 1,
      }}
      className="hidden md:block"
    >
      {shapes.map((s) => {
        const parallaxStyle: React.CSSProperties = {
          position: "absolute",
          left: `${s.x}%`,
          top: `${s.y}%`,
          opacity: s.opacity,
          transform: `rotate(${s.rotation}deg)`,
          animation: `blobFloat1 ${20 + s.speed * 100}s ease-in-out infinite`,
        };

        if (s.type === "circle") {
          return (
            <svg key={s.key} width={s.size} height={s.size} viewBox="0 0 40 40" style={parallaxStyle}>
              <circle cx="20" cy="20" r="18" fill="none" stroke="#D4A843" strokeWidth="1.5" />
            </svg>
          );
        }
        if (s.type === "diamond") {
          return (
            <svg key={s.key} width={s.size} height={s.size} viewBox="0 0 40 40" style={parallaxStyle}>
              <rect x="10" y="10" width="20" height="20" fill="rgba(212,168,67,0.05)" stroke="#D4A843" strokeWidth="1" transform="rotate(45 20 20)" />
            </svg>
          );
        }
        if (s.type === "plus") {
          return (
            <svg key={s.key} width={12} height={12} viewBox="0 0 12 12" style={parallaxStyle}>
              <line x1="6" y1="0" x2="6" y2="12" stroke="#D4A843" strokeWidth="1.5" />
              <line x1="0" y1="6" x2="12" y2="6" stroke="#D4A843" strokeWidth="1.5" />
            </svg>
          );
        }
        if (s.type === "dots") {
          return (
            <svg key={s.key} width={20} height={20} viewBox="0 0 20 20" style={parallaxStyle}>
              <circle cx="5" cy="5" r="2" fill="#D4A843" />
              <circle cx="15" cy="5" r="2" fill="#D4A843" />
              <circle cx="10" cy="15" r="2" fill="#D4A843" />
            </svg>
          );
        }
        /* line */
        return (
          <svg key={s.key} width={s.size} height={4} viewBox={`0 0 ${s.size} 4`} style={parallaxStyle}>
            <line x1="0" y1="2" x2={s.size} y2="2" stroke="#D4A843" strokeWidth="1" />
          </svg>
        );
      })}
    </div>
  );
}
